import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Archive, ArrowLeft, Loader, Pencil, Plus, Star, StarOff, Trash2, TriangleAlert, Waypoints } from 'lucide-react';
import type { Ruta, RutaBody } from '@tms/contratos';
import { api } from '../api';
import { EditorRuta } from '../components/EditorRuta';
import { Mapa, type Encuadre, type LineaMapa, type PuntoMapa } from '../components/Mapa';
import { TIPO_PARADA } from '../components/estados';
import { useSesion } from '../state/Sesion';
import { useToast } from '../state/Toast';
import { fmtDuracionMin, fmtFechaHora, fmtKm } from '../util/format';
import { useCarga } from '../util/hooks';
import { puedeEditarRutas } from '../util/roles';

/** Cuerpo de una edición que solo cambia banderas de la ruta (por defecto, activa). */
function cuerpoDe(r: Ruta, cambios: Partial<Pick<RutaBody, 'porDefecto' | 'activa'>>): RutaBody {
  return {
    nombre: r.nombre,
    puntosPaso: r.puntosPaso,
    paradas: r.paradas.map((p) => ({
      id: p.id,
      nombre: p.nombre,
      tipo: p.tipo,
      ubicacion: p.ubicacion,
      radioM: p.radioM,
      obligatoria: p.obligatoria,
      duracionMinEsperadaMin: p.duracionMinEsperadaMin,
      duracionMaxEsperadaMin: p.duracionMaxEsperadaMin,
    })),
    porDefecto: r.porDefecto,
    activa: r.activa,
    ...cambios,
  };
}

export function TroncalDetallePagina() {
  const { id } = useParams();
  const troncalId = Number(id);
  const { usuario } = useSesion();
  const { avisar } = useToast();
  const puedeEditar = puedeEditarRutas(usuario?.rol);
  const { datos: t, cargando, error, recargar } = useCarga(() => api.troncal(troncalId), [troncalId]);
  const [seleccionId, setSeleccionId] = useState<number | null>(null);
  /** undefined = no se edita; null = ruta nueva; Ruta = edición. */
  const [editando, setEditando] = useState<Ruta | null | undefined>(undefined);
  const [trabajando, setTrabajando] = useState(false);

  const rutas = t?.rutas ?? [];
  const seleccionada = rutas.find((r) => r.id === seleccionId) ?? rutas.find((r) => r.porDefecto) ?? rutas.find((r) => r.activa) ?? rutas[0] ?? null;

  const lineas = useMemo<LineaMapa[]>(
    () =>
      rutas.map((r) => ({
        id: `r-${r.id}`,
        variante: r.id === seleccionada?.id ? 'ruta-seleccionada' : !r.activa ? 'ruta-inactiva' : r.porDefecto ? 'ruta-defecto' : 'ruta',
        coords: r.traza.coordinates,
      })),
    [rutas, seleccionada?.id],
  );
  const puntos = useMemo<PuntoMapa[]>(
    () => [
      ...(seleccionada?.puntosPaso ?? []).map((p, i): PuntoMapa => ({
        id: `pp-${seleccionada?.id}-${i}`,
        lat: p.latitud,
        lon: p.longitud,
        variante: 'paso',
        texto: String(i + 1),
        titulo: p.nombre ? `Punto de paso ${i + 1}: ${p.nombre}` : `Punto de paso ${i + 1}`,
        detalle: p.nombre ? 'Punto de paso de la ruta' : 'Sin nombre',
      })),
      ...(seleccionada?.paradas ?? []).map((p) => ({
        id: `pr-${p.id}`,
        lat: p.ubicacion.latitud,
        lon: p.ubicacion.longitud,
        variante: `parada-${p.tipo}`,
        texto: String(p.orden),
        titulo: `${p.orden}. ${p.nombre}`,
        detalle: `${TIPO_PARADA[p.tipo].etiqueta} · radio ${p.radioM} m · ${p.obligatoria ? 'obligatoria' : 'opcional'}`,
      })),
    ],
    [seleccionada],
  );
  const encuadre = useMemo<Encuadre | undefined>(() => {
    const coords = (seleccionada ? [seleccionada] : rutas).flatMap((r) => r.traza.coordinates);
    return coords.length ? { clave: `${troncalId}-${seleccionada?.id ?? 0}`, puntos: coords, zoomMax: 12 } : undefined;
  }, [seleccionada, rutas, troncalId]);

  const ejecutar = async (fn: () => Promise<unknown>, ok: string) => {
    setTrabajando(true);
    try {
      await fn();
      avisar(ok);
      recargar();
    } catch (e) {
      avisar(e instanceof Error ? e.message : 'No se pudo completar la acción.', 'error');
    } finally {
      setTrabajando(false);
    }
  };

  if (cargando && !t)
    return (
      <div className="page">
        <div className="skeleton">
          <Loader className="spin" aria-hidden size={16} />
          Cargando troncal…
        </div>
      </div>
    );
  if (error || !t)
    return (
      <div className="page">
        <Link className="back" to="/troncales">
          <ArrowLeft aria-hidden size={14} />
          Volver a troncales
        </Link>
        <div className="banner error" role="alert" style={{ marginTop: 16 }}>
          <TriangleAlert aria-hidden />
          {error ?? 'No se encontró la troncal.'}
        </div>
      </div>
    );

  const activas = rutas.filter((r) => r.activa);

  return (
    <div className="page">
      <Link className="back" to="/troncales">
        <ArrowLeft aria-hidden size={14} />
        Volver a troncales
      </Link>
      <div className="viaje-head">
        <div>
          <div className="eyebrow">DESPACHO / TRONCALES / {t.identificacion}</div>
          <h1>
            {t.nombre}
            {t.archivado && (
              <span className="chip muted">
                <Archive aria-hidden />
                Archivada en Mara
              </span>
            )}
            {t.cantidadRutas === 0 && (
              <span className="chip amber">
                <TriangleAlert aria-hidden />
                Sin rutas
              </span>
            )}
          </h1>
          <div className="meta">
            {t.origen ?? '—'} → {t.destino ?? '—'} · referencia {fmtKm(t.kmReferencia)}
            {t.kmCombustible !== null && ` · combustible ${fmtKm(t.kmCombustible)}`}
            {t.clienteDefecto && ` · cliente por defecto: ${t.clienteDefecto.razonSocial}`}
            <br />
            Sincronizada desde Mara el {fmtFechaHora(t.sincronizadoEn)}. La troncal no se edita acá; las rutas sí.
          </div>
        </div>
        {puedeEditar && editando === undefined && !t.archivado && (
          <button type="button" className="primary" onClick={() => setEditando(null)}>
            <Plus aria-hidden size={14} />
            Nueva ruta
          </button>
        )}
      </div>

      {editando !== undefined ? (
        <EditorRuta key={editando?.id ?? 'nueva'} troncal={t} ruta={editando} onCerrar={() => setEditando(undefined)} onGuardada={recargar} />
      ) : (
        <div className="troncal-grid">
          <div>
            <section className="card" aria-label="Rutas de la troncal">
              <header>
                <h2>
                  <Waypoints aria-hidden />
                  Rutas
                </h2>
                <span className="muted" style={{ fontSize: 12 }}>
                  {activas.length} activa{activas.length === 1 ? '' : 's'}
                </span>
              </header>
              {rutas.length === 0 ? (
                <div className="vacio">
                  <Waypoints aria-hidden />
                  Esta troncal todavía no tiene rutas.
                  {puedeEditar && !t.archivado ? (
                    <div style={{ marginTop: 12 }}>
                      <button type="button" className="primary" onClick={() => setEditando(null)}>
                        <Plus aria-hidden size={14} />
                        Crear la primera ruta
                      </button>
                    </div>
                  ) : (
                    <div>Pedile al despacho que la rutee.</div>
                  )}
                </div>
              ) : (
                <div className="rutas-lista">
                  {rutas.map((r) => (
                    <div key={r.id} className={`ruta-item ${r.id === seleccionada?.id ? 'sel' : ''}`} role="button" tabIndex={0} onClick={() => setSeleccionId(r.id)} onKeyDown={(e) => e.key === 'Enter' && setSeleccionId(r.id)}>
                      <div className="fila">
                        <strong>{r.nombre}</strong>
                        {r.porDefecto && (
                          <span className="chip green">
                            <Star aria-hidden />
                            Por defecto
                          </span>
                        )}
                        {!r.activa && (
                          <span className="chip muted">
                            <Archive aria-hidden />
                            Dada de baja
                          </span>
                        )}
                      </div>
                      <small>
                        {fmtKm(r.km)} · {fmtDuracionMin(r.duracionMin)} · {r.paradas.length} parada{r.paradas.length === 1 ? '' : 's'} · actualizada {fmtFechaHora(r.actualizadoEn)}
                      </small>
                      {puedeEditar && (
                        <div className="editor-acciones" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="btn-sm" onClick={() => setEditando(r)}>
                            <Pencil aria-hidden size={13} />
                            Editar
                          </button>
                          {r.activa && !r.porDefecto && (
                            <button type="button" className="btn-sm" disabled={trabajando} onClick={() => void ejecutar(() => api.actualizarRuta(r.id, cuerpoDe(r, { porDefecto: true })), `«${r.nombre}» es ahora la ruta por defecto`)}>
                              <StarOff aria-hidden size={13} />
                              Marcar por defecto
                            </button>
                          )}
                          {r.activa && (
                            <button
                              type="button"
                              className="btn-sm"
                              disabled={trabajando}
                              title="Los viajes que ya la tienen asignada la conservan"
                              onClick={() => {
                                if (window.confirm(`¿Dar de baja la ruta «${r.nombre}»? Los viajes que ya la tienen asignada la conservan, pero no se va a ofrecer para viajes nuevos.`))
                                  void ejecutar(() => api.bajaRuta(r.id), 'Ruta dada de baja');
                              }}
                            >
                              <Trash2 aria-hidden size={13} />
                              Dar de baja
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!puedeEditar && rutas.length > 0 && <div className="foot">Solo despacho y administración pueden editar rutas.</div>}
            </section>

            {seleccionada && (
              <section className="card" aria-label="Paradas de la ruta">
                <header>
                  <h2>Paradas de «{seleccionada.nombre}»</h2>
                </header>
                {seleccionada.paradas.length === 0 ? (
                  <div className="vacio">La ruta no tiene paradas planificadas.</div>
                ) : (
                  <div className="table-wrap">
                    <table className="tabla">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Parada</th>
                          <th>Radio</th>
                          <th>Permanencia</th>
                          <th>Regla</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seleccionada.paradas.map((p) => {
                          const Icono = TIPO_PARADA[p.tipo].icono;
                          return (
                            <tr key={p.id}>
                              <td>{p.orden}</td>
                              <td>
                                <strong>{p.nombre}</strong>
                                <small style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                  <Icono aria-hidden size={11} />
                                  {TIPO_PARADA[p.tipo].etiqueta}
                                </small>
                              </td>
                              <td>{p.radioM} m</td>
                              <td>
                                {p.duracionMinEsperadaMin === null && p.duracionMaxEsperadaMin === null
                                  ? '—'
                                  : `${p.duracionMinEsperadaMin ?? 0} a ${p.duracionMaxEsperadaMin === null ? 'sin límite' : `${p.duracionMaxEsperadaMin} min`}`}
                              </td>
                              <td>{p.obligatoria ? <span className="chip amber">Obligatoria</span> : <span className="chip muted">Opcional</span>}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}
          </div>

          <section className="card" aria-label="Mapa de rutas" style={{ overflow: 'hidden' }}>
            <div className="editor-mapa">
              <Mapa lineas={lineas} puntos={puntos} encuadre={encuadre} />
            </div>
            <div className="foot">
              Línea verde: ruta por defecto · gris: otras rutas activas · punteada: dada de baja · clara y gruesa: la seleccionada.
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
